import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, AlertCircle, Building2, Clock, PenLine, X } from "lucide-react";
import { api } from "@/lib/api";

interface ListItem {
  id: string;
  folioRegistro: string | null;
  tipoPersona: string;
  razonSocial: string;
  nombreComercial: string | null;
  rfc: string;
  correoPrincipal: string;
  puedeActuarComoTitular: boolean;
  puedeActuarComoTramitador: boolean;
  estatus: string;
  sectoresCodigos: string[];
  tramitadoresRazonSocial: string[];
  cartasVigentes: number;
  representantesActivos: number;
  fechaCreacion: string;
  fechaEnvioRevision: string | null;
  diasEnRevision: number;
}

interface PageResp<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface SectorOption {
  id: string;
  codigo: string;
  nombre: string;
}

const PAGE_SIZE = 20;

type TabKey = "todos" | "borradores" | "revision" | "observados" | "vigentes" | "cerrados";

const TABS: { key: TabKey; label: string; estatuses: string[] }[] = [
  { key: "todos", label: "Todos", estatuses: [] },
  { key: "borradores", label: "Borradores", estatuses: ["BORRADOR_INTERNO"] },
  { key: "revision", label: "En revisión", estatuses: ["EN_REVISION_QA", "EN_REVISION_DOCUMENTAL", "SUBSANADO"] },
  { key: "observados", label: "Observados", estatuses: ["OBSERVADO", "PREVENCION_ENVIADA"] },
  { key: "vigentes", label: "Vigentes", estatuses: ["APROBADO", "ACTIVO", "MODIFICADO", "MODIFICACION_EN_PROCESO"] },
  { key: "cerrados", label: "Cerrados", estatuses: ["SUSPENDIDO", "BAJA", "CANCELADO", "RECHAZADO"] },
];

const SORT_OPCIONES: { value: string; label: string }[] = [
  { value: "razonSocial,asc", label: "Razón social (A-Z)" },
  { value: "razonSocial,desc", label: "Razón social (Z-A)" },
  { value: "rfc,asc", label: "RFC (A-Z)" },
  { value: "fechaCreacion,desc", label: "Más reciente" },
  { value: "fechaCreacion,asc", label: "Más antiguo" },
  { value: "fechaEnvioRevision,asc", label: "Más antiguo en revisión" },
  { value: "fechaEnvioRevision,desc", label: "Más reciente en revisión" },
];

const DEFAULT_SORT = "razonSocial,asc";

export function RegistrosLegalesListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = (searchParams.get("tab") as TabKey) || "todos";
  const appliedQ = searchParams.get("q") ?? "";
  const tipoPersona = searchParams.get("tipoPersona") ?? "";
  const sectorId = searchParams.get("sectorId") ?? "";
  const bandera = searchParams.get("bandera") ?? "";
  const sort = searchParams.get("sort") ?? DEFAULT_SORT;
  const page = Math.max(0, Number.parseInt(searchParams.get("page") ?? "0", 10) || 0);

  const [qInput, setQInput] = useState(appliedQ);
  useEffect(() => {
    setQInput(appliedQ);
  }, [appliedQ]);

  const tabDef = useMemo(() => TABS.find((t) => t.key === tab) ?? TABS[0], [tab]);

  const sectoresQuery = useQuery({
    queryKey: ["catalogo-sectores"],
    queryFn: async () => {
      const { data } = await api.get<SectorOption[]>("/api/catalogos/sectores");
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const listQuery = useQuery({
    queryKey: ["registros-legales", tab, appliedQ, tipoPersona, sectorId, bandera, sort, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("size", String(PAGE_SIZE));
      params.set("page", String(page));
      params.set("sort", sort);
      if (appliedQ) params.set("q", appliedQ);
      if (tipoPersona) params.set("tipoPersona", tipoPersona);
      if (sectorId) params.set("sectorId", sectorId);
      if (bandera) params.set("bandera", bandera);
      for (const est of tabDef.estatuses) params.append("estatus", est);
      const { data } = await api.get<PageResp<ListItem>>(`/api/registros-legales?${params.toString()}`);
      return data;
    },
  });

  const totalElements = listQuery.data?.totalElements ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 0;
  const hayFiltrosActivos =
    tab !== "todos" || !!appliedQ || !!tipoPersona || !!sectorId || !!bandera || sort !== DEFAULT_SORT;

  function updateParam(updates: Record<string, string | null>, opts: { resetPage?: boolean } = { resetPage: true }) {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(updates)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    if (opts.resetPage) next.delete("page");
    setSearchParams(next, { replace: false });
  }

  function limpiarFiltros() {
    setSearchParams(new URLSearchParams(), { replace: false });
    setQInput("");
  }

  function aplicarBusqueda() {
    updateParam({ q: qInput.trim() || null });
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Trámites Legales — Registros</h1>
          <p className="text-sm text-fg-muted">Consulta los registros legales capturados en SICEN.</p>
        </div>
        <Link
          to="/tramites-legales/alta"
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" />
          Alta de Registro Legal
        </Link>
      </header>

      <nav className="flex flex-wrap gap-1 border-b">
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => updateParam({ tab: t.key === "todos" ? null : t.key })}
              className={
                "border-b-2 px-3 py-2 text-sm font-medium transition-colors " +
                (active
                  ? "border-primary-strong text-primary-strong"
                  : "border-transparent text-fg-muted hover:border-edge-strong hover:text-fg")
              }
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          aplicarBusqueda();
        }}
        className="flex flex-wrap items-center gap-2 rounded-lg border bg-surface p-3"
      >
        <div className="flex flex-1 min-w-[260px] items-center gap-2 rounded border border-edge-strong bg-surface px-2">
          <Search className="h-4 w-4 text-fg-faint" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="RFC, razón social o correo…"
            className="flex-1 border-0 bg-transparent px-1 py-1.5 text-sm focus:outline-none"
          />
        </div>

        <select
          value={tipoPersona}
          onChange={(e) => updateParam({ tipoPersona: e.target.value || null })}
          className="rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          title="Tipo de persona"
        >
          <option value="">Tipo de persona</option>
          <option value="FISICA">Física</option>
          <option value="MORAL">Moral</option>
        </select>

        <select
          value={sectorId}
          onChange={(e) => updateParam({ sectorId: e.target.value || null })}
          className="rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          title="Sector"
          disabled={sectoresQuery.isLoading}
        >
          <option value="">Todos los sectores</option>
          {sectoresQuery.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.codigo} — {s.nombre}
            </option>
          ))}
        </select>

        <select
          value={bandera}
          onChange={(e) => updateParam({ bandera: e.target.value || null })}
          className="rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          title="Bandera"
        >
          <option value="">Todas las banderas</option>
          <option value="TITULAR">Titulares</option>
          <option value="TRAMITADOR">Tramitadores</option>
          <option value="AMBOS">Ambos</option>
        </select>

        <select
          value={sort}
          onChange={(e) => updateParam({ sort: e.target.value === DEFAULT_SORT ? null : e.target.value })}
          className="rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          title="Orden"
        >
          {SORT_OPCIONES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <button type="submit" className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary-hover">
          Buscar
        </button>

        {hayFiltrosActivos && (
          <button
            type="button"
            onClick={limpiarFiltros}
            className="inline-flex items-center gap-1 rounded border px-2 py-1.5 text-sm text-fg-muted hover:bg-surface-2"
            title="Limpiar todos los filtros"
          >
            <X className="h-3.5 w-3.5" />
            Limpiar
          </button>
        )}
      </form>

      <div className="rounded-lg border bg-surface shadow-card">
        {listQuery.isLoading && <div className="p-6 text-sm text-fg-subtle">Cargando…</div>}
        {listQuery.error && (
          <div className="flex items-start gap-2 p-4 text-sm text-estado-warn">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            Error cargando los registros.
          </div>
        )}
        {listQuery.data && (
          <>
            <div className="flex items-center justify-between border-b px-4 py-2 text-xs uppercase tracking-wide text-fg-subtle">
              <span>{totalElements} registros</span>
              {totalPages > 1 && (
                <span>
                  Página {page + 1} de {totalPages}
                </span>
              )}
            </div>
            {listQuery.data.content.length === 0 ? (
              <div className="flex items-center gap-3 p-6 text-sm text-fg-subtle">
                <Building2 className="h-5 w-5" />
                No se encontraron registros con los filtros actuales.
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y text-sm">
                <thead className="bg-surface-2">
                  <tr>
                    {["Razón social", "RFC", "Tipo persona", "Banderas", "Estatus", "Días", "Cartas", "Reps", ""].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y bg-surface">
                  {listQuery.data.content.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/registros-legales/${r.id}`)}
                      className="cursor-pointer hover:bg-surface-2"
                    >
                      <td className="px-4 py-2 align-top">
                        <span className="font-medium text-fg">{r.razonSocial}</span>
                        {r.nombreComercial && (
                          <div className="text-xs text-fg-subtle">{r.nombreComercial}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 align-top font-mono text-xs">{r.rfc}</td>
                      <td className="px-4 py-2 align-top text-xs">{r.tipoPersona}</td>
                      <td className="px-4 py-2 align-top text-xs">
                        <div className="flex flex-wrap gap-1">
                          {r.puedeActuarComoTitular && (
                            <Badge tone="info">
                              {r.tramitadoresRazonSocial.length === 0 ? "Titular directo" : "Titular"}
                            </Badge>
                          )}
                          {r.puedeActuarComoTramitador && <Badge tone="info">Tramitador</Badge>}
                          {r.tramitadoresRazonSocial.map((t) => (
                            <Badge key={t} tone="neutral">Con tramitador: {t}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 align-top">
                        <Badge tone={toneEstatus(r.estatus)}>{r.estatus}</Badge>
                      </td>
                      <td className="px-4 py-2 align-top text-xs">
                        {r.fechaEnvioRevision ? <DiasChip dias={r.diasEnRevision} /> : <span className="text-fg-faint">—</span>}
                      </td>
                      <td className="px-4 py-2 align-top text-center text-xs">{r.cartasVigentes}</td>
                      <td className="px-4 py-2 align-top text-center text-xs">{r.representantesActivos}</td>
                      <td className="px-4 py-2 align-top text-right">
                        {r.estatus === "BORRADOR_INTERNO" && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/mi-registro-legal/wizard?id=${r.id}&paso=1`);
                            }}
                            className="inline-flex items-center gap-1 rounded border border-primary-strong px-2 py-1 text-xs font-medium text-primary-strong hover:bg-estado-info-bg"
                            title="Abrir el wizard de 4 pasos para continuar este borrador"
                          >
                            <PenLine className="h-3 w-3" />
                            Continuar borrador
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t bg-surface-2 px-4 py-2 text-sm">
                <button
                  type="button"
                  disabled={page <= 0}
                  onClick={() => updateParam({ page: String(page - 1) }, { resetPage: false })}
                  className="rounded border bg-surface px-3 py-1 text-xs text-fg-muted hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ← Anterior
                </button>
                <span className="text-xs text-fg-subtle">
                  Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalElements)} de {totalElements}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages - 1}
                  onClick={() => updateParam({ page: String(page + 1) }, { resetPage: false })}
                  className="rounded border bg-surface px-3 py-1 text-xs text-fg-muted hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function toneEstatus(estatus: string): "ok" | "info" | "warn" | "neutral" {
  if (estatus === "APROBADO" || estatus === "ACTIVO") return "ok";
  if (estatus === "RECHAZADO" || estatus === "SUSPENDIDO" || estatus === "BAJA" || estatus === "CANCELADO")
    return "warn";
  if (estatus === "BORRADOR_INTERNO") return "neutral";
  return "info";
}

/**
 * Indicador de días en revisión con código de color para SLA. ≤2 días neutral,
 * 3-5 ámbar, >5 rojo. Sólo se renderiza cuando el RL está en flujo de revisión
 * (fechaEnvioRevision no es null).
 */
function DiasChip({ dias }: { dias: number }) {
  const tone = dias > 5 ? "bg-estado-err-bg text-estado-err" : dias > 2 ? "bg-estado-warn-bg text-estado-warn" : "bg-estado-block-bg text-estado-block";
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
      <Clock className="h-3 w-3" />
      {dias}d
    </span>
  );
}

function Badge({ tone, children }: { tone: "ok" | "info" | "warn" | "neutral"; children: React.ReactNode }) {
  const cls = {
    ok: "bg-estado-ok-bg text-estado-ok",
    info: "bg-estado-info-bg text-estado-info",
    warn: "bg-estado-warn-bg text-estado-warn",
    neutral: "bg-estado-block-bg text-estado-block",
  }[tone];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}
